import React from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaEnvelope, FaLinkedin, FaDownload } from 'react-icons/fa';
import './About.css';

const EMAIL = 'geoara07@gmail.com';
const LINKEDIN = 'https://www.linkedin.com/in/george-arampatzis';
const GITHUB = 'https://github.com/justsubway';

const About = () => {
  const techStack = {
    languages: [
      { name: 'TypeScript', badge: 'https://img.shields.io/badge/TypeScript-3178C6?logo=typescript&logoColor=white' },
      { name: 'JavaScript', badge: 'https://img.shields.io/badge/JavaScript-F7DF1E?logo=javascript&logoColor=black' },
      { name: 'Java', badge: 'https://img.shields.io/badge/Java-%23ED8B00.svg?logo=openjdk&logoColor=white' },
      { name: 'Python', badge: 'https://img.shields.io/badge/Python-3670A0?logo=python&logoColor=ffdd54' },
      { name: 'C++', badge: 'https://img.shields.io/badge/C++-00599C?logo=cplusplus&logoColor=white' },
    ],
    tools: [
      { name: 'React', badge: 'https://img.shields.io/badge/React-20232A?logo=react&logoColor=61DAFB' },
      { name: 'React Native', badge: 'https://img.shields.io/badge/React%20Native-20232A?logo=react&logoColor=61DAFB' },
      { name: 'Node.js', badge: 'https://img.shields.io/badge/Node.js-339933?logo=nodedotjs&logoColor=white' },
      { name: 'Expo', badge: 'https://img.shields.io/badge/Expo-000020?logo=expo&logoColor=white' },
      { name: '.NET', badge: 'https://img.shields.io/badge/.NET-512BD4?logo=dotnet&logoColor=white' },
      { name: 'Firebase', badge: 'https://img.shields.io/badge/Firebase-FFCA28?logo=firebase&logoColor=black' },
      { name: 'Tailwind', badge: 'https://img.shields.io/badge/Tailwind-38B2AC?logo=tailwindcss&logoColor=white' },
      { name: 'Git', badge: 'https://img.shields.io/badge/Git-F05032?logo=git&logoColor=white' },
    ],
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="about" id="about">
      <motion.div
        className="about-content"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ amount: 0.2 }}
      >
        <motion.h2 className="section-title" variants={itemVariants}>
          About Me
        </motion.h2>

        <div className="about-layout">
          <motion.div className="profile-hero" variants={itemVariants}>
            <div className="profile-container">
              <div className="profile-image-wrapper">
                <img
                  src={process.env.PUBLIC_URL + '/profile-full.jpg'}
                  alt="George Arampatzis"
                  className="profile-image"
                />
                <div className="profile-glow"></div>
              </div>
              <div className="profile-info">
                <h3 className="profile-name">George Arampatzis</h3>
                <p className="profile-title">
                  Full-Stack Developer · CS Student · Athens, Greece
                </p>
                <div className="status-indicator">
                  <span className="status-dot"></span>
                  <span className="status-text">
                    Open to internships & junior roles
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="content-grid">
            <motion.div className="main-content" variants={itemVariants}>
              <div className="content-card">
                <h4 className="content-title">Who I am</h4>
                <div className="content-text">
                  <p>
                    Self-taught since 13, I build full-stack web and mobile products
                    with a focus on clean design and code that ships. I study Computer
                    Science at the University of Piraeus and previously worked as a
                    Front-End Developer at Parkaroo.
                  </p>
                  <p>
                    I move fast without creating mess, care about both aesthetics and
                    architecture, and thrive in autonomous, results-driven teams.
                  </p>
                </div>
              </div>

              <div className="content-card">
                <h4 className="content-title">Experience</h4>
                <div className="content-text">
                  <p>
                    <strong>Junior Front-End Developer · Parkaroo</strong> (Mar 2026–Jul 2026)
                    — UI features for a smart parking platform with React Native,
                    React, and Expo.
                  </p>
                  <p>
                    <strong>Full-Stack Intern · Parkaroo</strong> (Oct 2025–Jan 2026)
                    — Frontend and backend with React, React Native, Expo, .NET, and Java.
                  </p>
                  <p>
                    <strong>Web Developer · For The Locals</strong> (Sep 2023–Present)
                    — Sole developer for a clothing brand’s Shopify storefront:
                    responsive layouts, subscriptions, and checkout.
                  </p>
                </div>
              </div>

              <div className="content-card">
                <h4 className="content-title">Assessments</h4>
                <div className="content-text">
                  <p>
                    <strong>Alva Labs Logic Test</strong> — 95th percentile analytical
                    and abstract reasoning.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div className="tech-sidebar" variants={itemVariants}>
              <div className="tech-card">
                <h4 className="tech-title">Languages</h4>
                <div className="tech-badges">
                  {techStack.languages.map((tech) => (
                    <img
                      key={tech.name}
                      src={tech.badge}
                      alt={tech.name}
                      className="tech-badge"
                    />
                  ))}
                </div>
              </div>

              <div className="tech-card">
                <h4 className="tech-title">Frameworks & Tools</h4>
                <div className="tech-badges">
                  {techStack.tools.map((tech) => (
                    <img
                      key={tech.name}
                      src={tech.badge}
                      alt={tech.name}
                      className="tech-badge"
                    />
                  ))}
                </div>
              </div>

              <div className="contact-links">
                <a
                  href={GITHUB}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                  title="GitHub"
                >
                  <FaGithub />
                  <span>GitHub</span>
                </a>
                <a
                  href={LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                  title="LinkedIn"
                >
                  <FaLinkedin />
                  <span>LinkedIn</span>
                </a>
                <a href={`mailto:${EMAIL}`} className="contact-link" title="Email">
                  <FaEnvelope />
                  <span>Email</span>
                </a>
                <a
                  href={process.env.PUBLIC_URL + '/George_Arampatzis.pdf'}
                  download="George_Arampatzis_CV.pdf"
                  className="contact-link"
                  title="Download Resume"
                >
                  <FaDownload />
                  <span>Resume</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default About;
